import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Check, X, HelpCircle, Loader2, AlertCircle, RotateCcw, Upload, ChevronDown, Lightbulb, ZoomIn, Copy, Share2 } from 'lucide-react';
import { Layout } from '../components/Layout';
import { AuthedImage, getCachedImageUrl } from '../components/AuthedImage';
import { LightboxGallery } from '../components/Lightbox';
import { api, getUser } from '../lib/api';
import { retryRecognition } from '../lib/retry';
import { formatDuration } from '../lib/format';

type AuditEvent = {
  action: string;
  after_json: string | null;
  created_at: number;
};

type Verdict = 'correct' | 'wrong' | 'unknown';
type Question = {
  no: number | string;
  expr: string;
  user_answer?: string | null;
  correct_answer?: string | null;
  verdict: Verdict;
  note?: string;
};
type Group = { label?: string; page?: string; questions: Question[] };
type ImageBlock = {
  index: number;
  layout?: string;
  identifiable?: boolean;
  issues?: string[];
  groups: Group[];
};
type Result = {
  rejected: boolean;
  reject_reason: string | null;
  images: ImageBlock[];
  summary: { total: number; correct: number; wrong: number; unknown: number };
};
type Recognition = {
  id: number;
  status: 'pending' | 'done' | 'failed' | 'rejected';
  rejected: number;
  reject_reason: string | null;
  result: Result | null;
  summary_total: number | null;
  summary_correct: number | null;
  summary_wrong: number | null;
  summary_unknown: number | null;
  created_at: number;
  completed_at: number | null;
};
type UploadedImageMeta = {
  image_index: number;
  mime_type: string;
  byte_size: number;
  width: number | null;
  height: number | null;
  original_name: string | null;
  cleaned: number;
};

export function ResultPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<Recognition | null>(null);
  const [images, setImages] = useState<UploadedImageMeta[]>([]);
  const [zoomIdx, setZoomIdx] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'not_correct' | 'wrong' | 'unknown'>('all');
  const [filterTouched, setFilterTouched] = useState(false);
  const [showCorrect, setShowCorrect] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const v = window.localStorage.getItem('result.show_correct');
    return v === null ? true : v === '1';
  });
  useEffect(() => {
    window.localStorage.setItem('result.show_correct', showCorrect ? '1' : '0');
  }, [showCorrect]);
  const pickFilter = (f: 'all' | 'not_correct' | 'wrong' | 'unknown') => {
    setFilter(f);
    setFilterTouched(true);
  };
  const [copyState, setCopyState] = useState<'idle' | 'ok' | 'err'>('idle');
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    const startedAt = Date.now();

    async function tick() {
      if (cancelled) return;
      const r = await api<{ recognition: Recognition; images: UploadedImageMeta[] }>(
        `/api/v1/recognitions/${id}`,
      );
      if (cancelled) return;
      setLoading(false);
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
      if (!r.ok) {
        setError(r.error.message);
        return;
      }
      setData(r.data.recognition);
      setImages(r.data.images ?? []);
      if (r.data.recognition.status === 'pending') {
        // 轮询:头 30s 每 2s,之后每 4s,超过 10 分钟放弃
        const interval = Date.now() - startedAt < 30_000 ? 2000 : 4000;
        if (Date.now() - startedAt < 10 * 60_000) {
          pollTimer = setTimeout(tick, interval);
        }
      }
    }
    tick();
    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [id]);

  if (loading && !data) {
    return (
      <Layout>
        <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-slate-500">
          <Loader2 className="h-8 w-8 animate-spin text-sky-600" />
          <p className="text-sm">读取记录中…</p>
        </div>
      </Layout>
    );
  }

  if (data && data.status === 'pending') {
    return (
      <Layout>
        <PendingView recognitionId={data.id} createdAt={data.created_at} images={images} elapsedFallback={elapsed} />
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout>
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-red-700">
          {error ?? '无数据'}
        </div>
      </Layout>
    );
  }

  if (data.status === 'failed') {
    const durationSec =
      data.completed_at && data.completed_at > data.created_at
        ? data.completed_at - data.created_at
        : null;
    return (
      <Layout>
        <FailedView recognitionId={Number(id)} images={images} durationSec={durationSec} />
      </Layout>
    );
  }

  if (data.rejected || data.status === 'rejected') {
    return (
      <Layout>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <div>
              <p className="font-medium">这不像珠心算题目图片</p>
              <p className="mt-1 text-sm">
                {data.reject_reason ?? '模型无法识别为珠心算练习题。'}
              </p>
              <Link
                to="/app"
                className="mt-3 inline-flex h-9 items-center gap-1 rounded-md bg-sky-600 px-3 text-sm font-medium text-white hover:bg-sky-700"
              >
                重新选择
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const result = data.result ?? {
    rejected: false,
    reject_reason: null,
    images: [],
    summary: { total: 0, correct: 0, wrong: 0, unknown: 0 },
  };

  // 智能默认:若用户没手动切换过且存在非正确题,默认只看非正确(老师讲题场景)
  const autoDefaulted =
    !filterTouched &&
    filter === 'all' &&
    (result.summary.wrong + result.summary.unknown) > 0;
  const effectiveFilter = autoDefaulted ? 'not_correct' : filter;

  const buildShareText = (scope: 'not_correct' | 'all') => {
    const lines: string[] = [];
    lines.push(
      `共 ${result.summary.total} 题 · 正确 ${result.summary.correct} · 错误 ${result.summary.wrong} · 存疑 ${result.summary.unknown}`,
    );
    for (const img of result.images) {
      if (img.groups.length === 0) continue;
      const imgHead = result.images.length > 1 ? `【第 ${img.index} 张】` : '';
      for (const g of img.groups) {
        const filtered = g.questions.filter((q) =>
          scope === 'all' ? true : q.verdict !== 'correct',
        );
        if (filtered.length === 0) continue;
        const groupLabel = [g.label, g.page === 'left' ? '左页' : g.page === 'right' ? '右页' : '']
          .filter(Boolean)
          .join(' ');
        lines.push('');
        lines.push(`${imgHead}${groupLabel}`.trim());
        for (const q of filtered) {
          const mark = q.verdict === 'correct' ? '✓' : q.verdict === 'wrong' ? '✗' : '?';
          const mine = q.user_answer ?? '—';
          const ref = q.correct_answer ?? '—';
          if (showCorrect) {
            lines.push(`${q.no}. ${q.expr} = ${mine} ${mark}  [正确 ${ref}]`);
          } else {
            lines.push(`${q.no}. ${q.expr} = ${mine} ${mark}`);
          }
        }
      }
    }
    return lines.join('\n').trim();
  };

  const onCopy = async (scope: 'not_correct' | 'all') => {
    const text = buildShareText(scope);
    try {
      await navigator.clipboard.writeText(text);
      setCopyState('ok');
    } catch {
      setCopyState('err');
    }
    setTimeout(() => setCopyState('idle'), 1800);
  };

  const onShare = async () => {
    const text = buildShareText(result.summary.wrong + result.summary.unknown > 0 ? 'not_correct' : 'all');
    const nav = navigator as Navigator & {
      share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
    };
    if (typeof nav.share === 'function') {
      try {
        await nav.share({ title: '珠心算识别结果', text });
        return;
      } catch {
        /* user cancel */
      }
    }
    onCopy('not_correct');
  };

  return (
    <Layout>
      <div className="space-y-6">
        <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h1 className="text-lg font-semibold text-slate-900">识别完成</h1>
            <span className="text-sm text-slate-500">
              共 <span className="font-semibold text-slate-700">{result.summary.total}</span> 题
              {result.summary.total > 0 && (
                <>
                  {' · '}正确率{' '}
                  <span className="font-semibold text-slate-700">
                    {Math.round((result.summary.correct / result.summary.total) * 100)}%
                  </span>
                </>
              )}
              {data.completed_at && data.completed_at > data.created_at && (
                <span className="ml-2 text-xs text-slate-400">
                  · 耗时 {formatDuration(data.completed_at - data.created_at)}
                </span>
              )}
            </span>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3 no-print">
            <button
              type="button"
              onClick={() => pickFilter('all')}
              className={
                'rounded-lg bg-emerald-50 p-4 text-center transition-all hover:shadow-sm ' +
                (effectiveFilter === 'all' ? 'ring-2 ring-emerald-400 ring-offset-1' : '')
              }
            >
              <div className="text-3xl font-semibold text-emerald-600 sm:text-4xl">
                {result.summary.correct}
              </div>
              <div className="mt-1 text-xs text-emerald-700">正确 · 看全部</div>
            </button>
            <button
              type="button"
              onClick={() => pickFilter('wrong')}
              className={
                'rounded-lg bg-red-50 p-4 text-center transition-all hover:shadow-sm ' +
                (effectiveFilter === 'wrong' ? 'ring-2 ring-red-400 ring-offset-1' : '')
              }
            >
              <div className="text-3xl font-semibold text-red-600 sm:text-4xl">
                {result.summary.wrong}
              </div>
              <div className="mt-1 text-xs text-red-700">错误 · 点看仅错题</div>
            </button>
            <button
              type="button"
              onClick={() => pickFilter('unknown')}
              className={
                'rounded-lg bg-amber-50 p-4 text-center transition-all hover:shadow-sm ' +
                (effectiveFilter === 'unknown' ? 'ring-2 ring-amber-400 ring-offset-1' : '')
              }
            >
              <div className="text-3xl font-semibold text-amber-600 sm:text-4xl">
                {result.summary.unknown}
              </div>
              <div className="mt-1 text-xs text-amber-700">无法判断 · 点看仅存疑</div>
            </button>
          </div>
          {/* 打印时的静态三列 */}
          <div className="mt-4 hidden grid-cols-3 gap-3 print:grid">
            <div className="rounded-lg bg-emerald-50 p-4 text-center">
              <div className="text-3xl font-semibold text-emerald-600">{result.summary.correct}</div>
              <div className="mt-1 text-xs text-emerald-700">正确</div>
            </div>
            <div className="rounded-lg bg-red-50 p-4 text-center">
              <div className="text-3xl font-semibold text-red-600">{result.summary.wrong}</div>
              <div className="mt-1 text-xs text-red-700">错误</div>
            </div>
            <div className="rounded-lg bg-amber-50 p-4 text-center">
              <div className="text-3xl font-semibold text-amber-600">{result.summary.unknown}</div>
              <div className="mt-1 text-xs text-amber-700">无法判断</div>
            </div>
          </div>
          {result.summary.total > 0 &&
            result.summary.unknown / result.summary.total > 0.3 && (
              <p className="mt-3 rounded-md bg-amber-50/60 px-3 py-2 text-xs text-amber-800">
                有较多题目无法判断,建议换一张更清晰、光线更好的照片重新上传。
              </p>
            )}
        </section>

        {images.length > 0 && (
          <section className="space-y-2 no-print">
            <h2 className="text-sm font-medium text-slate-600">原始照片</h2>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
              {images.map((img) => (
                <button
                  key={img.image_index}
                  type="button"
                  disabled={img.cleaned === 1}
                  onClick={() => setZoomIdx(img.image_index)}
                  className="group relative aspect-square overflow-hidden rounded-md border border-slate-200 bg-slate-50 disabled:cursor-not-allowed"
                  aria-label={`放大第 ${img.image_index} 张`}
                >
                  {img.cleaned === 1 ? (
                    <span className="flex h-full w-full items-center justify-center text-xs text-slate-400">
                      已清理
                    </span>
                  ) : (
                    <>
                      <AuthedImage
                        recognitionId={data.id}
                        imageIndex={img.image_index}
                        className="h-full w-full object-cover"
                      />
                      <span className="pointer-events-none absolute inset-0 hidden items-center justify-center bg-slate-900/30 text-white group-hover:flex">
                        <ZoomIn className="h-5 w-5" />
                      </span>
                      <span className="pointer-events-none absolute bottom-1 left-1 rounded bg-slate-900/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
                        {img.image_index}
                      </span>
                    </>
                  )}
                </button>
              ))}
            </div>
          </section>
        )}

        {autoDefaulted && (
          <div className="flex items-center justify-between rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800 no-print">
            <span>已自动只显示"非正确"题(错题 + 存疑),便于讲解。</span>
            <button
              type="button"
              onClick={() => pickFilter('all')}
              className="font-medium text-sky-700 underline-offset-2 hover:underline"
            >
              看全部
            </button>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 no-print">
          <div className="inline-flex flex-wrap rounded-md border border-slate-200 bg-white p-0.5">
            {(['all', 'not_correct', 'wrong', 'unknown'] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => pickFilter(f)}
                className={
                  'h-9 rounded px-3 text-sm font-medium transition-colors ' +
                  (effectiveFilter === f ? 'bg-sky-600 text-white' : 'text-slate-600 hover:bg-slate-50')
                }
              >
                {f === 'all' ? '全部' : f === 'not_correct' ? '非正确' : f === 'wrong' ? '仅错题' : '仅存疑'}
              </button>
            ))}
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={showCorrect}
              onChange={(e) => setShowCorrect(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
            />
            显示正确答案
          </label>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => onCopy(result.summary.wrong + result.summary.unknown > 0 ? 'not_correct' : 'all')}
            className="inline-flex h-9 items-center gap-1 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 hover:bg-slate-50"
            title={
              showCorrect
                ? '复制文本(含正确答案),可粘贴到微信/备忘录'
                : '复制文本(不含正确答案,让孩子自己订正)'
            }
          >
            {copyState === 'ok' ? (
              <>
                <Check className="h-4 w-4 text-emerald-600" />
                已复制
              </>
            ) : copyState === 'err' ? (
              <>
                <AlertCircle className="h-4 w-4 text-red-600" />
                复制失败
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                复制错题
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onShare}
            className="inline-flex h-9 items-center gap-1 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 hover:bg-slate-50"
            title="发送到微信 / 群 / 其他 app"
          >
            <Share2 className="h-4 w-4" />
            分享
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            打印
          </button>
          <Link
            to="/app"
            className="h-9 rounded-md bg-sky-600 px-3 text-sm font-medium text-white hover:bg-sky-700 inline-flex items-center"
          >
            继续识别
          </Link>
        </div>

        {result.images.map((img) => {
          const hasIssues = img.issues && img.issues.length > 0;
          const unidentifiable = img.identifiable === false || img.groups.length === 0;
          return (
            <section key={img.index} className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-base font-semibold text-slate-900">第 {img.index} 张照片</h3>
                {hasIssues && !unidentifiable && (
                  <span
                    className="text-xs text-slate-400 no-print"
                    title={img.issues?.join('、')}
                  >
                    拍摄有小瑕疵,结果仅供参考
                  </span>
                )}
              </div>
              {unidentifiable ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <p className="font-medium">这张照片未能识别</p>
                  {hasIssues ? (
                    <ul className="mt-2 list-disc space-y-0.5 pl-5">
                      {img.issues!.map((x, i) => (
                        <li key={i}>{x}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-1">模型判定这张照片不可靠识别。</p>
                  )}
                  <p className="mt-2 text-amber-800">
                    建议:只对准**一页**、光线充足、尽量与纸面垂直后重新上传这一页。
                  </p>
                </div>
              ) : (
                img.groups.map((g, gi) => (
                  <div key={gi} className="space-y-2">
                    {(g.label || (g.page && g.page !== 'single')) && (
                      <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-500">
                        {g.label && (
                          <span>{g.label.replace(/\s*[((][^))]+[))]\s*$/u, '').trim()}</span>
                        )}
                        {g.page === 'left' && (
                          <span className="inline-flex items-center rounded-md border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs text-sky-700">
                            左页
                          </span>
                        )}
                        {g.page === 'right' && (
                          <span className="inline-flex items-center rounded-md border border-violet-200 bg-violet-50 px-2 py-0.5 text-xs text-violet-700">
                            右页
                          </span>
                        )}
                      </div>
                    )}
                    <ul className="divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200 bg-white">
                      {g.questions
                        .filter((q) => {
                          if (effectiveFilter === 'all') return true;
                          if (effectiveFilter === 'not_correct') return q.verdict !== 'correct';
                          return q.verdict === effectiveFilter;
                        })
                        .map((q, qi) => (
                          <QuestionRow key={qi} q={q} showCorrect={showCorrect} />
                        ))}
                    </ul>
                  </div>
                ))
              )}
            </section>
          );
        })}
      </div>
      {zoomIdx !== null && images.length > 0 && (
        <LightboxGallery
          total={images.length}
          startIndex={Math.max(0, images.findIndex((m) => m.image_index === zoomIdx))}
          onClose={() => setZoomIdx(null)}
          renderAt={(i) => (
            <ZoomedFrame
              recognitionId={data.id}
              imageIndex={images[i]?.image_index ?? i + 1}
            />
          )}
        />
      )}
    </Layout>
  );
}

function ZoomedFrame({
  recognitionId,
  imageIndex,
}: {
  recognitionId: number;
  imageIndex: number;
}) {
  const [url, setUrl] = useState<string | null>(getCachedImageUrl(recognitionId, imageIndex));
  if (url) {
    return (
      <img
        src={url}
        alt={`第 ${imageIndex} 张照片`}
        className="max-h-full max-w-full rounded-md object-contain shadow-2xl"
      />
    );
  }
  return (
    <>
      <AuthedImage
        recognitionId={recognitionId}
        imageIndex={imageIndex}
        className="hidden"
        onReady={(u) => setUrl(u)}
      />
      <div className="flex items-center gap-2 text-white/80">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">加载中…</span>
      </div>
    </>
  );
}

function PendingView({
  recognitionId,
  createdAt,
  images,
  elapsedFallback,
}: {
  recognitionId: number;
  createdAt: number;
  images: UploadedImageMeta[];
  elapsedFallback: number;
}) {
  const [now, setNow] = useState(Math.floor(Date.now() / 1000));
  useEffect(() => {
    const t = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(t);
  }, []);
  const visibleImages = images.filter((m) => !m.cleaned);
  const elapsed = Math.max(elapsedFallback, now - createdAt);
  const TYPICAL_MIN = 30;
  const TYPICAL_MAX = 120;
  const pct = Math.min(95, Math.round((elapsed / TYPICAL_MAX) * 100));
  const overtime = elapsed > TYPICAL_MAX;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-sky-100 bg-gradient-to-br from-sky-50 to-white p-5 text-sky-900">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-sky-100">
            <Loader2 className="h-5 w-5 animate-spin text-sky-600" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold">AI 正在识别</p>
            <p className="mt-1 text-sm text-sky-900/80">
              你可以关掉这一页,识别会在后台继续完成。回到"历史记录"随时查看结果。
            </p>
            <div className="mt-3">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-sky-100">
                <div
                  className="h-full rounded-full bg-sky-500 transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-sky-800/80">
                <span>已等 {formatDuration(elapsed)}</span>
                <span>
                  {overtime
                    ? '比通常稍慢,模型可能较忙,请再耐心等一会'
                    : `通常 ${TYPICAL_MIN}~${TYPICAL_MAX} 秒`}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {visibleImages.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-sm font-medium text-slate-700">
            正在识别这 {visibleImages.length} 张照片
          </p>
          <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-5">
            {visibleImages.map((m) => (
              <div
                key={m.image_index}
                className="relative aspect-square overflow-hidden rounded-md border border-slate-200 bg-slate-50"
              >
                <AuthedImage
                  recognitionId={recognitionId}
                  imageIndex={m.image_index}
                  className="h-full w-full object-cover"
                />
                <span className="pointer-events-none absolute left-1 top-1 rounded bg-slate-900/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
                  第 {m.image_index} 张
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FailedView({
  recognitionId,
  images,
  durationSec,
}: {
  recognitionId: number;
  images: UploadedImageMeta[];
  durationSec: number | null;
}) {
  const navigate = useNavigate();
  const user = getUser();
  const isAdmin = user?.role === 'admin';
  const [audit, setAudit] = useState<AuditEvent[] | null>(null);
  const [expandIndex, setExpandIndex] = useState<number | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [zoomIdx, setZoomIdx] = useState<number | null>(null);
  const visibleImages = images.filter((m) => !m.cleaned);

  const onRetry = async () => {
    if (retrying) return;
    setRetrying(true);
    setRetryError(null);
    const r = await retryRecognition(recognitionId);
    if (r.ok) {
      navigate(`/result/${r.newId}`);
    } else {
      setRetrying(false);
      setRetryError(r.message);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      const aRes = await api<{ items: AuditEvent[] }>(
        `/api/v1/recognitions/${recognitionId}/audit`,
      );
      if (aRes.ok) setAudit(aRes.data.items);
    })();
  }, [recognitionId, isAdmin]);

  const attempts = audit
    ? audit.filter((e) => {
        try {
          const p = JSON.parse(e.after_json ?? '{}') as Record<string, unknown>;
          return typeof p.attempt === 'number';
        } catch {
          return false;
        }
      }).length
    : null;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-900">
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-base font-semibold">这次没能识别出结果</p>
            <p className="mt-1 text-sm">
              {isAdmin && attempts && attempts > 0
                ? `系统自动重试了 ${attempts} 次,仍然没能完成识别。`
                : '系统已经自动重试了几次,仍然没能完成识别。'}
              {durationSec !== null && (
                <span className="ml-1 text-red-800/70">耗时 {formatDuration(durationSec)}。</span>
              )}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={onRetry}
                disabled={retrying}
                className="inline-flex h-9 items-center gap-1 rounded-md bg-sky-600 px-3 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {retrying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                {retrying ? '正在启动…' : '重新识别(同一张照片)'}
              </button>
              <Link
                to="/app"
                className="inline-flex h-9 items-center gap-1 rounded-md border border-sky-200 bg-white px-3 text-sm font-medium text-sky-700 hover:bg-sky-50"
              >
                <Upload className="h-4 w-4" />
                重新上传
              </Link>
              <Link
                to="/history"
                className="inline-flex h-9 items-center rounded-md border border-red-200 bg-white px-3 text-sm font-medium text-red-700 hover:bg-red-50"
              >
                查看历史
              </Link>
            </div>
            {retryError && (
              <p className="mt-2 text-sm text-red-700">启动重新识别失败:{retryError}</p>
            )}
          </div>
        </div>
      </div>

      {visibleImages.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-sm font-medium text-slate-700">
            这次上传的照片({visibleImages.length} 张)
          </p>
          <p className="mt-1 text-xs text-slate-500">点图可放大查看,判断是不是拍糊或遮挡。</p>
          <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-5">
            {visibleImages.map((m) => (
              <button
                key={m.image_index}
                type="button"
                onClick={() => setZoomIdx(m.image_index)}
                className="group relative aspect-square overflow-hidden rounded-md border border-slate-200 bg-slate-50 hover:border-sky-300"
                aria-label={`放大第 ${m.image_index} 张`}
              >
                <AuthedImage
                  recognitionId={recognitionId}
                  imageIndex={m.image_index}
                  className="h-full w-full object-cover"
                />
                <span className="pointer-events-none absolute inset-0 hidden items-center justify-center bg-slate-900/30 text-white group-hover:flex">
                  <ZoomIn className="h-6 w-6" />
                </span>
                <span className="pointer-events-none absolute left-1 top-1 rounded bg-slate-900/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
                  第 {m.image_index} 张
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-sky-100 bg-sky-50/60 p-5 text-sky-900">
        <div className="flex items-start gap-3">
          <Lightbulb className="mt-0.5 h-5 w-5 flex-shrink-0 text-sky-600" />
          <div>
            <p className="text-sm font-medium">可以这样试试</p>
            <ul className="mt-2 space-y-1 text-sm text-sky-900/90">
              <li>• 换一张更清晰的照片(光线足、对齐整页、没有阴影)</li>
              <li>• 确认题目与答案都在画面内,未被手指或反光遮挡</li>
              <li>• 稍等 1~2 分钟后再试一次,模型偶尔会临时繁忙</li>
              <li>• 多次仍失败请截图找管理员,我们会帮忙排查</li>
            </ul>
          </div>
        </div>
      </div>

      {isAdmin && (
        <details className="rounded-xl border border-slate-200 bg-white p-4 open:shadow-sm">
          <summary className="cursor-pointer text-sm font-medium text-slate-700">
            技术详情(仅管理员可见)
          </summary>
          {!audit ? (
            <p className="mt-3 text-sm text-slate-400">加载中…</p>
          ) : audit.length === 0 ? (
            <p className="mt-3 text-sm text-slate-400">暂无事件</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {audit.map((e, i) => {
                let parsed: Record<string, unknown> = {};
                try {
                  parsed = JSON.parse(e.after_json ?? '{}') as Record<string, unknown>;
                } catch {
                  /* ignore */
                }
                const attempt = parsed.attempt;
                const dur = parsed.duration_ms;
                const ok = parsed.ok;
                const err =
                  typeof parsed.error === 'string'
                    ? parsed.error
                    : parsed.error
                      ? String(parsed.error)
                      : '';
                const contentLen = parsed.content_len;
                const rawPreview =
                  typeof parsed.raw_preview === 'string' ? parsed.raw_preview : '';
                return (
                  <li key={i} className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
                    <div className="flex flex-wrap items-center gap-2 text-slate-700">
                      <span className="inline-flex h-5 items-center rounded bg-slate-200 px-2 font-mono text-xs">
                        {e.action}
                      </span>
                      {typeof attempt === 'number' && (
                        <span className="text-xs text-slate-500">attempt={String(attempt)}</span>
                      )}
                      {typeof dur === 'number' && (
                        <span className="text-xs text-slate-500">
                          {Math.round((dur as number) / 1000)}s
                        </span>
                      )}
                      {typeof ok === 'boolean' && (
                        <span
                          className={
                            'text-xs font-medium ' +
                            (ok ? 'text-emerald-600' : 'text-red-600')
                          }
                        >
                          {ok ? 'OK' : 'FAIL'}
                        </span>
                      )}
                      {typeof contentLen === 'number' && Number(contentLen) > 0 && (
                        <span className="text-xs text-slate-500">
                          content={String(contentLen)}ch
                        </span>
                      )}
                      <span className="ml-auto text-xs text-slate-400">
                        {new Date(e.created_at * 1000).toLocaleTimeString('zh-CN', {
                          hour12: false,
                        })}
                      </span>
                    </div>
                    {err.length > 0 && (
                      <p className="mt-1 break-all text-xs text-red-600">错误: {err}</p>
                    )}
                    {rawPreview && (
                      <button
                        type="button"
                        onClick={() => setExpandIndex((p) => (p === i ? null : i))}
                        className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
                      >
                        <ChevronDown
                          className={
                            'h-3 w-3 transition-transform ' +
                            (expandIndex === i ? 'rotate-180' : '')
                          }
                        />
                        {expandIndex === i ? '收起' : '展开'}原始响应
                      </button>
                    )}
                    {expandIndex === i && rawPreview && (
                      <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all rounded bg-white p-2 font-mono text-xs text-slate-700">
                        {rawPreview}
                      </pre>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </details>
      )}
      {zoomIdx !== null && visibleImages.length > 0 && (
        <LightboxGallery
          total={visibleImages.length}
          startIndex={Math.max(0, visibleImages.findIndex((m) => m.image_index === zoomIdx))}
          onClose={() => setZoomIdx(null)}
          renderAt={(i) => (
            <ZoomedFrame
              recognitionId={recognitionId}
              imageIndex={visibleImages[i]?.image_index ?? i + 1}
            />
          )}
        />
      )}
    </div>
  );
}

function QuestionRow({ q, showCorrect }: { q: Question; showCorrect: boolean }) {
  const verdict = q.verdict;
  const cls =
    verdict === 'correct'
      ? 'text-emerald-700'
      : verdict === 'wrong'
        ? 'text-red-700'
        : 'text-amber-700';
  const Icon = verdict === 'correct' ? Check : verdict === 'wrong' ? X : HelpCircle;
  const label = verdict === 'correct' ? '正确' : verdict === 'wrong' ? '错误' : '存疑';
  const badgeCls =
    'inline-flex flex-shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ' +
    (verdict === 'correct'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : verdict === 'wrong'
        ? 'border-red-200 bg-red-50 text-red-700'
        : 'border-amber-200 bg-amber-50 text-amber-700');
  return (
    <li
      className={
        'result-row flex flex-col gap-1 px-4 py-3 text-sm sm:flex-row sm:items-center sm:gap-3 ' +
        (verdict === 'wrong' ? 'wrong bg-red-50/40' : '')
      }
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="w-6 flex-shrink-0 text-right text-slate-400">{String(q.no)}</span>
        <span className="expr flex-1 break-all font-mono text-slate-900">{q.expr}</span>
        <span className={badgeCls + ' sm:hidden'}>
          <Icon className="h-3 w-3" />
          {label}
        </span>
      </div>
      <div className="flex items-center justify-end gap-3 pl-9 sm:pl-0">
        <span className={badgeCls + ' hidden sm:inline-flex'}>
          <Icon className="h-3 w-3" />
          {label}
        </span>
        <span className={'w-16 text-right font-mono ' + cls}>
          <span className="mr-1 text-[10px] font-normal text-slate-400 sm:hidden">答</span>
          {q.user_answer ?? '—'}
        </span>
        {showCorrect && (
          <span className="w-16 text-right font-mono text-slate-500">
            <span className="mr-1 text-[10px] font-normal text-slate-400 sm:hidden">正确</span>
            {q.correct_answer ?? '—'}
          </span>
        )}
      </div>
    </li>
  );
}
